import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Building2, Calendar, MapPin, Briefcase, ChevronRight, Star, Check } from 'lucide-react';

interface Experience {
  company: string;
  role: string;
  location: string;
  period: string;
  current?: boolean;
  description: string[];
  technologies: string[];
  achievements?: string[];
}

const WorkExperience: React.FC = () => {
  const experiences: Experience[] = [
    {
      company: "Outback Webworks",
      role: "Laravel, React, Next.js Developer",
      location: "Australia (Remote)",
      period: "Dec 2024 – Present",
      current: true,
      description: [
        "Developing web applications using React, Laravel, and Next.js",
        "Collaborating with a distributed team to deliver scalable solutions",
        "Leveraging 5+ technologies for full-stack development"
      ],
      technologies: ["React", "Laravel", "Next.js", "Full-Stack Development"],
      achievements: ["Leading multiple full-stack projects", "Over a year of successful remote collaboration (December 2024–Present)"]
    },
    {
      company: "Outback Webworks",
      role: "WordPress Developer",
      location: "Australia (Remote)",
      period: "Oct 2024 – Nov 2024",
      description: [
        "Built and customized WordPress websites for client projects",
        "Optimized performance and implemented responsive design"
      ],
      technologies: ["WordPress", "PHP", "Responsive Design", "Performance Optimization"]
    },
    {
      company: "Dancing Code",
      role: "Web Developer",
      location: "Czech Republic",
      period: "Mar 2023 – Sep 2024",
      description: [
        "Developed responsive web applications using PHP, JavaScript, jQuery, Laravel, and Nette",
        "Resolved technical issues, debugged code, and maintained high-quality standards",
        "Designed and integrated REST APIs for seamless service communication",
        "Optimized MySQL databases for better performance",
        "Parsed XML data and improved CSV processing modules",
        "Automated tasks via cron jobs",
        "Integrated Shoptet with Shopify and developed Shoptet plugins",
        "Modified Shoptet frontend using JavaScript"
      ],
      technologies: ["PHP", "Laravel", "Nette", "JavaScript", "jQuery", "MySQL", "REST APIs", "Shoptet", "Shopify"],
      achievements: ["Successfully integrated multiple e-commerce platforms", "Improved database performance significantly"]
    },
    {
      company: "Palawan News",
      role: "Web Developer",
      location: "Palawan, Philippines",
      period: "Oct 2020 – Jul 2021",
      description: [
        "Developed and maintained the company website using WordPress",
        "Created and deployed custom plugins"
      ],
      technologies: ["WordPress", "PHP", "Custom Plugin Development"]
    },
    {
      company: "Province of Palawan - Kalayaan",
      role: "Web Developer / Software Developer",
      location: "Kalayaan, Palawan",
      period: "Jul 2019 – Sep 2020",
      description: [
        "Developed websites using WordPress",
        "Built an employment record system using C#"
      ],
      technologies: ["WordPress", "C#", "System Development"]
    },
    {
      company: "Freelance Projects",
      role: "Web Developer",
      location: "Project-Based Work",
      period: "Jan 2012 – Jun 2019",
      description: [
        "Designed and developed web, mobile, and software applications",
        "Used diverse technologies including PHP, WordPress, Laravel, Flutter, React Native, C#, and VB .Net"
      ],
      technologies: ["PHP", "WordPress", "Laravel", "Flutter", "React Native", "C#", "VB .Net"],
      achievements: ["Successfully delivered 500+ projects", "Built expertise across multiple platforms"]
    },
    {
      company: "City Peso of Puerto Princesa",
      role: "Software Developer",
      location: "Puerto Princesa, Palawan",
      period: "May 2010 – Jan 2011",
      description: [
        "Created an employer–jobseeker matching system using Visual Basic"
      ],
      technologies: ["Visual Basic", "Desktop Application Development"]
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <section id="experience" className="py-20 bg-slate-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={stagger}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-600 border border-blue-200 mb-4"
            >
              <Check /> Career Journey
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Work <span className="text-blue-600">Experience</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Over 8 years of professional experience working with companies across Australia, 
              Czech Republic, and the Philippines, delivering quality solutions remotely and on-site.
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-blue-500 transform md:-translate-x-0.5"></div>

            {/* Experience Cards */}
            <div className="space-y-12">
              {experiences.map((exp, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Timeline Dot */}
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-blue-500 rounded-full transform -translate-x-2 md:-translate-x-2 shadow-lg z-10">
                    {exp.current && (
                      <div className="absolute inset-0 w-4 h-4 bg-blue-400 rounded-full animate-ping"></div>
                    )}
                  </div>

                  {/* Content Card */}
                  <div className={`w-full md:w-5/12 ml-16 md:ml-0 ${
                    index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                  }`}>
                    <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl hover:shadow-2xl transition-all duration-300 group overflow-hidden">
                      <CardContent className="p-0">
                        {/* Header */}
                        <div className={`p-6 ${
                          exp.current 
                            ? 'bg-green-600' 
                            : 'bg-blue-600'
                        } text-white`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <Building2 className="h-5 w-5" />
                              <h3 className="text-xl font-bold">{exp.company}</h3>
                            </div>
                            {exp.current && (
                              <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold">
                                Current
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2 mb-2">
                            <Briefcase className="h-4 w-4" />
                            <p className="font-semibold">{exp.role}</p>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm opacity-90">
                            <div className="flex items-center space-x-2">
                              <MapPin className="h-4 w-4" />
                              <span>{exp.location}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4" />
                              <span>{exp.period}</span>
                            </div>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                          {/* Description */}
                          <ul className="space-y-2 mb-6">
                            {exp.description.map((desc, descIndex) => (
                              <li key={descIndex} className="flex items-start space-x-2 text-gray-700">
                                <ChevronRight className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                                <span className="leading-relaxed">{desc}</span>
                              </li>
                            ))}
                          </ul>

                          {/* Achievements */}
                          {exp.achievements && (
                            <div className="mb-6">
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                <Star className="h-4 w-4 text-yellow-500 mr-2" />
                                Key Achievements
                              </h4>
                              <ul className="space-y-1">
                                {exp.achievements.map((achievement, achIndex) => (
                                  <li key={achIndex} className="text-gray-600 text-sm flex items-start space-x-2">
                                    <ChevronRight className="h-3 w-3 text-yellow-500 mt-1 flex-shrink-0" />
                                    <span>{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Technologies */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Technologies Used</h4>
                            <div className="flex flex-wrap gap-2">
                              {exp.technologies.map((tech, techIndex) => (
                                  <span
                                  key={techIndex}
                                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium border border-blue-200"
                                >
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            variants={fadeInUp}
            className="text-center mt-16 p-8 bg-blue-50 rounded-3xl border border-blue-200"
          >
            <h4 className="text-2xl font-bold text-gray-900 mb-4">
              Ready for Your Next Project?
            </h4>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              With 5+ years of diverse experience across multiple industries and technologies, 
              I'm ready to bring that expertise to your next project.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-blue-600 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Let's Work Together
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default WorkExperience;